<template>
    <AppModal
        :title="
            data?.title ??
            (data?.editing ? 'Update project' : 'Create new project')
        "
        :submitText="data?.editing ? 'Update Project' : 'Create Project'"
        :scroll="false"
        @submit="onSubmit"
        @close="() => forceClose()"
    >
        <template #content>
            <div class="flex min-h-0 flex-1 flex-col">
                <ShadowScrollContainer>
                    <fieldset class="border-3d-b p-6 pb-4 space-y-4">
                <TeamSelector
                    v-if="!data?.editing && isEmployee()"
                    v-model="form.team_id"
                    label="Customer"
                    :error="form.errors?.team_id"
                />
                <div v-if="data?.editing &&  isEmployee()">
                    <div class="text-sm leading-tight">

                        <h4 class="type-primary font-medium">
                            {{ data.project.team.customer_reference.customer_id }} • {{ data.project.team.customer_reference.erp_record.name }}
                        </h4>
                        <p class="type-secondary text-xs">{{ data.project.team.name }}</p>
                    </div>
                </div>

                <div class="flex gap-4 -mt-2">
                    <AppInput
                        type="text"
                        v-model="form.label"
                        :error="form.errors?.label"
                        label="Project Name"
                        placeholder="e.g. Athena Hotel"
                        :required="true"
                        :autofocus="true"
                        class="grow"
                    />
                    <fieldset>
                        <legend class="text-sm type-secondary py-2">
                            Color
                        </legend>
                        <div class="inline-flex gap-2">
                            <label v-for="color in projectColors">
                                <input
                                    type="radio"
                                    name="color"
                                    v-model="form.options.color"
                                    :value="color.value"
                                    class="peer sr-only"
                                />
                                <span
                                    class="w-6 h-6 rounded-full flex items-center justify-center outline-offset-2 outline-2"
                                    :class="{
                                        [color.class]: true,
                                        outline:
                                            form.options.color === color.value,
                                        'peer-focus:outline peer-focus:outline-royal': true,
                                    }"
                                >
                                    <BaseSvg
                                        name="icon-check"
                                        class="w-4 h-4 text-white"
                                        v-if="
                                            form.options.color === color.value
                                        "
                                    />
                                </span>
                                <span class="sr-only">{{ color.label }}</span>
                            </label>
                        </div>
                        <ValidationError :text="form.errors['options.color']" />
                    </fieldset>
                </div>
                <AppInput
                    type="textarea"
                    v-model="form.meta.description"
                    :error="form.errors['meta.description']"
                    label="Description"
                    placeholder="e.g. Athena Hotel"
                    maxlength="200"
                />
                <div class="w-full grid grid-cols-2 gap-4 items-end">
                    <AppInput
                        type="date"
                        v-model="form.meta.bid_date"
                        :error="form.errors['meta.bid_date']"
                        label="Bid date"
                    />
                    <div @blur.capture="onBudgetBlur">
                        <AppInput
                            type="text"
                            v-model="budgetDisplay"
                            :error="form.errors['meta.budget']"
                            label="Budget"
                            icon="attach-money"
                            inputmode="decimal"
                        />
                    </div>
                </div>

                <div v-if="!data.project || data.project.user_id === user.id">
                    <div class="mt-6">
                        <toggle
                            label="Set as private"
                            v-model="form.is_private"
                            showLabel="true"
                            class="text-sm"
                        />
                        <p class="text-xs text-gray-625 dark:text-gray-350">
                            Projects are normally visible to all members of your organization.<br>Marking it as private will mean it is only visible to you.</p>
                    </div>
                    <ValidationError    
                        :text="form.errors?.is_private"
                        class="mt-1"
                    />
                </div>
            </fieldset>
            <fieldset class="px-6 pt-2 pb-6 space-y-4">
                <AppInput
                    label="Job reference number"
                    type="text"
                    v-model="form.meta.job_reference_number"
                    :error="form.errors['meta.job_reference_number']"
                />
                <AppInput
                    label="Architect"
                    type="text"
                    v-model="form.meta.architect"
                    :error="form.errors['meta.architect']"
                />
                <AppInput
                    label="General contractor"
                    type="text"
                    v-model="form.meta.general_contractor"
                    :error="form.errors['meta.general_contractor']"
                />
                <div class="w-full grid grid-cols-2 gap-4 items-end">
                    <AppInput
                        label="Starts on"
                        type="date"
                        v-model="form.meta.start_date"
                        :error="form.errors['meta.start_date']"
                    />
                    <AppInput
                        label="Ends on"
                        type="date"
                        v-model="form.meta.end_date"
                        :error="form.errors['meta.end_date']"
                    />
                </div>
            </fieldset>
            <!-- TODO: Add sets and openings creation -->
            <fieldset
                class="border-3d-t px-6 py-4 space-y-4"
                v-if="false && !data?.editing"
            >
                <div class="border-3d-b pb-6">
                    <h4 class="font-medium text-sm">Create sets</h4>
                    <div class="flex gap-4 items-end mt-1.5">
                        <AppInput
                            label="Qty"
                            type="number"
                            v-model="form.sets_quantity"
                            class="w-24"
                            :hide-optional="true"
                        />
                        <SeclockMultiselect
                            class="w-full"
                            v-model="form.sets_naming"
                            name="sortBy"
                            :options="bulkNamingConventionOptions"
                            :option-is-object="true"
                            :large="true"
                            :label="$t('Default naming convention')"
                            :can-clear="false"
                        />
                    </div>
                </div>
                <div>
                    <h4 class="font-medium text-sm">Create openings</h4>
                    <div class="flex gap-4 items-end mt-1.5">
                        <AppInput
                            label="Qty"
                            type="number"
                            v-model="form.openings_quantity"
                            class="w-24"
                            :hide-optional="true"
                        />
                        <SeclockMultiselect
                            class="w-full"
                            v-model="form.openings_naming"
                            name="sortBy"
                            :options="bulkNamingConventionOptions"
                            :option-is-object="true"
                            :large="true"
                            :label="$t('Default naming convention')"
                            :can-clear="false"
                        />
                    </div>
                </div>
            </fieldset>
                </ShadowScrollContainer>
            </div>
        </template>
    </AppModal>
</template>
<script setup>
import { v1 as uuidv1 } from 'uuid'
import {
    AppInput,
    AppModal,
    BaseSvg,
    FileUpload,
    ShadowScrollContainer,
    TeamSelector,
    Toggle,
    ValidationError,
} from '@/Components'
import { eventBus, useDialog, useUser } from '@/Composables'
import { useForm } from '@inertiajs/vue3'
import {inject, onMounted, ref, watch} from 'vue'


const id = uuidv1();
const { forceClose } = useDialog();
const { user, isEmployee } = useUser();
const { data } = defineProps({
    data: {
        type: Object,
        required: true,
        default: () => {},
    },
});

onMounted(() => {
    eventBus.on("form:reset", () => {
        form.reset();
        budgetDisplay.value = formatBudget(data.project?.meta.budget);
    });
});

const form = useForm({
    team_id: data.project?.team_id ?? null,
    label: data.project?.label ?? "",
    is_private: data.project?.is_private ?? false,
    options: {
        color: data.project?.options.color ?? "gray",
    },
    meta: {
        description: data.project?.meta.description ?? null,
        bid_date: data.project?.meta.bid_date ?? null,
        budget: data.project?.meta.budget ?? null,
        job_reference_number: data.project?.meta.job_reference_number ?? null,
        architect: data.project?.meta.architect ?? null,
        general_contractor: data.project?.meta.general_contractor ?? null,
        start_date: data.project?.meta.start_date ?? null,
        end_date: data.project?.meta.end_date ?? null,
    },
    docs: [],
});

const budgetFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function parseBudget(value) {
    if (value === '' || value == null) return null;
    const num = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return Number.isNaN(num) ? null : num;
}

function formatBudget(value) {
    const num = parseBudget(value);
    return num !== null ? budgetFormatter.format(num) : '';
}

const budgetDisplay = ref(formatBudget(data.project?.meta.budget));

watch(budgetDisplay, (newValue) => {
    form.meta.budget = parseBudget(newValue);
});

function onBudgetBlur() {
    const parsed = parseBudget(budgetDisplay.value);
    budgetDisplay.value = parsed !== null ? formatBudget(parsed) : '';
}

let route = inject("route");

const projectColors = [
    { value: "gray", label: "Gray", class: "bg-project-gray" },
    { value: "blue", label: "Blue", class: "bg-project-blue" },
    { value: "lavender", label: "Lavender", class: "bg-project-lavender" },
    { value: "orange", label: "Orange", class: "bg-project-orange" },
    { value: "avocado", label: "Avocado", class: "bg-project-avocado" },
];

function onSubmit(resolve, reject) {
    // Clear error messages before re-submitting
    form.clearErrors();

    const callbacks = {
        onSuccess: resolve,
        onError: reject,
        onFinish: () => eventBus.emit("dashboard:reload-card", 'project_card')

    };

    if (data?.editing) {
        form.put(route("web.account.projects.update", data.project), {
            only: ["referer", "project", "errors", "component"],
            ...callbacks,
        });
    } else {
        form.post(route("web.account.projects.store"), {
            ...callbacks,
        });
    }
}
</script>
